import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { axiosClient } from "@/lib/axios";
import { HttpError } from "@/types/error";
import { PublicInvitation, WithPagination } from "@classview/shared-types";
import {
  UseQueryOptions,
  keepPreviousData,
  useQuery,
} from "@tanstack/react-query";

const getInvitations = async ({
  organisationId,
  pageSize = 10,
  pageIndex = 0,
  search,
}: {
  organisationId: string;
  pageSize?: number;
  pageIndex?: number;
  search?: string;
}): Promise<WithPagination<PublicInvitation>> => {
  const offset = pageIndex * pageSize;

  const response = await axiosClient.get(`/${organisationId}/invitations`, {
    params: {
      limit: pageSize,
      offset,
      ...(search && { search: search }),
    },
  });
  return response.data;
};

const getInvitationsAdmin = async ({
  organisationId,
  pageSize = 10,
  pageIndex = 0,
  search,
}: {
  organisationId?: string;
  pageSize?: number;
  pageIndex?: number;
  search?: string;
}): Promise<WithPagination<PublicInvitation>> => {
  const offset = pageIndex * pageSize;

  const response = await axiosClient.get(`/invitations`, {
    params: {
      limit: pageSize,
      offset,
      ...(organisationId && { orgId: organisationId }),
      ...(search && { search: search }),
    },
  });
  return response.data;
};

export function useGetInvitations(
  {
    pageSize,
    pageIndex,
    search,
  }: { pageSize?: number; pageIndex?: number; search?: string | null },
  queryOptions?: Partial<
    UseQueryOptions<WithPagination<PublicInvitation>, HttpError>
  >,
) {
  const { organisation } = useOrganisation();
  return useQuery({
    queryKey: ["invitations", organisation.id, pageIndex, pageSize, search],
    queryFn: () =>
      getInvitations({
        organisationId: organisation.id,
        pageSize,
        pageIndex,
        search: search ?? undefined,
      }),
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
}

export function useGetInvitationsAdmin(
  {
    pageSize,
    pageIndex,
    search,
    organisationId,
  }: {
    organisationId?: string;
    pageSize?: number;
    pageIndex?: number;
    search?: string | null;
  },
  queryOptions?: Partial<
    UseQueryOptions<WithPagination<PublicInvitation>, HttpError>
  >,
) {
  return useQuery({
    queryKey: [
      "invitations",
      organisationId ?? "sa-view",
      pageIndex,
      pageSize,
      search,
    ],
    queryFn: () =>
      getInvitationsAdmin({
        organisationId,
        pageSize,
        pageIndex,
        search: search ?? undefined,
      }),
    ...queryOptions,
  });
}
